import { colorHelpers } from '@/utils/colorHelpers';

export function ProgressBar({
  steps,
  current,
  color,
}: {
  steps: { id: string; label: string }[];
  current: number;
  color: string;
}) {
  const c = colorHelpers(color);
  const total = steps.length;
  const safeCurrent = Math.min(Math.max(current, 0), total - 1);
  const currentStep = steps[safeCurrent];
  const progressPct = total > 1 ? ((safeCurrent + 1) / total) * 100 : 100;

  return (
    <div className="w-full min-w-0">
      {/* Mobile / tablette : barre compacte */}
      <div className="md:hidden space-y-3">
        <div className="flex items-center justify-between gap-3">
          <span className={`text-xs font-bold uppercase tracking-wide ${c.text}`}>
            Étape {safeCurrent + 1} / {total}
          </span>
          <span className="text-sm font-semibold text-gray-800 truncate text-right">
            {currentStep?.label}
          </span>
        </div>

        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full ${c.bg} rounded-full transition-all duration-300 ease-out`}
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="flex items-center gap-1">
          {steps.map((s, i) => (
            <div
              key={s.id}
              className={`flex-1 h-1 rounded-full transition-colors ${
                i < safeCurrent ? c.bg : i === safeCurrent ? c.bg : 'bg-gray-200'
              } ${i === safeCurrent ? 'opacity-80' : ''}`}
              title={s.label}
            />
          ))}
        </div>
      </div>

      {/* Desktop : stepper complet */}
      <div className="hidden md:block">
        <div className="flex items-center w-full min-w-0">
          {steps.map((s, i) => (
            <div key={s.id} className={`flex items-center min-w-0 ${i < steps.length - 1 ? 'flex-1' : ''}`}>
              <div
                className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                  i <= safeCurrent ? `${c.bg} text-white` : 'bg-gray-200 text-gray-500'
                }`}
                title={s.label}
              >
                {i < safeCurrent ? (
                  <i className="ri-check-line text-xs" />
                ) : (
                  i + 1
                )}
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`flex-1 h-1 mx-1.5 min-w-[6px] rounded transition-colors ${
                    i < safeCurrent ? c.bg : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="mt-3 grid gap-1 text-center" style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}>
          {steps.map((s, i) => (
            <span
              key={s.id}
              className={`text-[11px] leading-tight px-0.5 truncate ${
                i <= safeCurrent ? `${c.text} font-semibold` : 'text-gray-400'
              }`}
              title={s.label}
            >
              {s.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
